/**
 * resolveFormDeriveInputs
 *
 * The stamp's `inputs` array: the sorted, DISTINCT canonical operand paths one
 * entry reads — a field source, an algebra operand, a projection's partition key
 * and each of its column arms. Config-shaped by design, so the reconciler's
 * element-wise drift comparison against a stored stamp stays cheap however many
 * rows a projection covers.
 *
 * Takes the version because a `referenceProp` path embeds the `entityRecordId`
 * its version-level reference resolves to, under the rule
 * `entityReferences[key].entityId ?? key`. That is as far as the promise goes: a
 * projection's `rowReferenceProp` resolves a record per ROW, so `inputs` names
 * operand SHAPES and only the per-row hash material names documents — the weaker
 * promise `FormDeriveStamp.inputs` states, and the only one a per-row identity
 * can support.
 */

import { compareFormDeriveText } from './compareFormDeriveText';
import { formDeriveOperandPaths } from './formDeriveOperandPaths';
import type { FormDeriveEntry } from '../System.FormDerive.types';
import type { FormDeriveVersionInput } from './computeFormDerives';
import type { FormDeriveOperandSource } from './formDeriveOperandPaths';

function resolveFormDeriveInputs(
  entry: FormDeriveEntry,
  version: FormDeriveVersionInput,
): string[] {
  const sources: FormDeriveOperandSource[] =
    'operands' in entry
      ? entry.operands.map((operand) => operand.source)
      : 'columns' in entry
        ? [
            entry.source,
            ...entry.columns.map(({ value }) => ({ ...value, tableId: entry.source.tableId })),
          ]
        : [entry.source];
  return [
    ...new Set(
      sources.flatMap((source) => formDeriveOperandPaths(source, version.entityReferences)),
    ),
  ].sort(compareFormDeriveText);
}

export { resolveFormDeriveInputs };
